import { getCommonAxiosCommon, UrlBuilder } from 'app/helpers/MiscUtils';
import { JobFair, JobFairStatus, JobFairWithCompanyName, NgAPIResponse, Sponsor } from 'app/types';
import axios from 'axios';
import { config } from 'config';
import moment from 'moment';

import { TimeService } from './TimeService';

const timeService = new TimeService();

const EndPoints = {
  JobFairs: `${config.vjfpNGRestAPI}/organizations/:companyId/jobfairs`,
  JobFairById: `${config.vjfpNGRestAPI}/organizations/:companyId/jobfairs/:id`,
  CreatePaymentLink: `${config.vjfpNGRestAPI}/organizations/:companyId/jobfairs/:id/create-payment-link`
};

export class JobfairService {
      private publicBaseUrl = `${config.vjfpNGRestAPI}/public`;

      async createJobFair (companyId: string, jobFair: Omit<JobFair, 'id' | 'companyId' | 'createdAt' | 'paymentStatus' | 'status'>): Promise<JobFair> {
        const result = await axios.post<NgAPIResponse<JobFair>>(
          EndPoints.JobFairs,
          { ...jobFair, companyId },
          await getCommonAxiosCommon()
        );
        return result.data.data;
      }

      async getJobFairById (companyId: string, id: string): Promise<JobFair | undefined> {
        const result = await axios.get<NgAPIResponse<JobFair>>(
          UrlBuilder(EndPoints.JobFairById, { companyId, id }),
          await getCommonAxiosCommon()
        );
        return this.applyStatus(result.data.data);
      }

      async updateJobFair (jobFair: JobFair): Promise<JobFair> {
        const result = await axios.put<NgAPIResponse<JobFair>>(
          UrlBuilder(EndPoints.JobFairById, { companyId: jobFair.companyId, id: jobFair.id }),
          jobFair,
          await getCommonAxiosCommon()
        );
        return result.data.data;
      }

      async deleteJobFair (companyId: string, id: string): Promise<void> {
        await axios.delete(
          UrlBuilder(EndPoints.JobFairById, { companyId, id }),
          await getCommonAxiosCommon()
        );
      }

      async getJobFairs (companyId: string): Promise<JobFair[]> {
        const result = await axios.get<NgAPIResponse<JobFair[]>>(
          UrlBuilder(EndPoints.JobFairs, { companyId }),
          await getCommonAxiosCommon()
        );
        return result.data.data ? (Object.keys(result.data.data).length ? result.data.data : []) : [];
      }

      async createPaymentLink (companyId: string, id: string, redirectUrl?: string): Promise<string> {
        const response = await axios.post<NgAPIResponse<{url:string}>>(
          UrlBuilder(EndPoints.CreatePaymentLink, { companyId, id }),
          {
            redirectUrl
          },
          await getCommonAxiosCommon()
        );
        return response?.data?.data?.url;
      }

      async applyStatus (item: JobFair): Promise<JobFair | undefined> {
        if (!item) return item;

        // 1. Parse event time with correct timezone offset (EST = UTC-5)
        const timeZoneOffset = item.timezone === 'EST' ? '-0500' : '-0400';
        const startTime = moment(
      `${item.startDate} ${item.startTime} ${timeZoneOffset}`,
      'YYYY-MM-DD HH:mm ZZ' // Use 24-hour format for HH:mm
        ).utc();

        const endTime = startTime.clone()
          .add(item.durationHours, 'hours')
          .add(item.durationMinutes, 'minutes');
        const extendedEndTime = endTime.clone().add(1.5, 'hours');

        // 2. Get current time in UTC
        let currentDateTime: moment.Moment;
        const localStorageDate = localStorage.getItem('currentDate');
        const localStorageTime = localStorage.getItem('currentTime');

        if (localStorageDate && localStorageTime) {
          // Parse as local browser time (e.g., IST) and convert to UTC
          currentDateTime = moment(
        `${localStorageDate} ${localStorageTime}`,
        'YYYY-MM-DD HH:mm'
          ).utc();
        } else {
          const serverTime = moment.unix((await timeService.getServerTime()) || new Date().getTime());
          currentDateTime = moment(serverTime).utc();
        }

        // 3. Compare in UTC with inclusivity ([] includes start/end boundaries)
        let status = JobFairStatus.Ended;
        if (currentDateTime.isBetween(startTime, endTime, undefined, '[]')) {
          status = JobFairStatus.Started;
        } else if (currentDateTime.isBetween(endTime, extendedEndTime, undefined, '[]')) {
          status = JobFairStatus.ClosingSession;
        } else if (currentDateTime.isBefore(startTime)) {
          status = JobFairStatus.Upcoming;
        }

        return { ...item, status };
      }

      async getJobfairsAndSponsorBySubdomain (subdomain: string): Promise<{ sponsor: Sponsor, jobFairs: JobFairWithCompanyName[] }> {
        const result = await axios.get<NgAPIResponse<{ sponsor: Sponsor, jobFairs: JobFairWithCompanyName[] }>>(
            `${this.publicBaseUrl}/jobfairs/bySubdomain/${subdomain}`);

        if (!result.data?.isSuccess) {
          console.error(result.data.errors);
          throw new Error('Failed to get sponsor by subdomain');
        }

        return result.data.data;
      }
}
